import { useCallback, useMemo, useState } from 'react'
import { Box, Stack } from '@mui/material'

import {
  AriaInteractionMode,
  AssistantSpeakerRole,
  isSpokenAriaMode
} from '../../../../../../pure-js/types/AppAssistant'
import type { GoalScope } from '../../../../../../pure-js/types/CoachTypes'
import { getAriaTranscriptSpeaker } from '../../../../../../pure-js/libs/ariaTranscript'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { DefaultModal } from '../../../../shared/components'
import { TESTIDS } from '../../../../config/testids'
import { useCoachConversations, useCoachStatus, useUserGoals } from '../../../../apiLayer/queryHooks/coach'
import { useUser } from '../../../../apiLayer/queryHooks/queries'
import useAppState from '../../../../hooks/useAppState'
import { hasIncompleteOnboardingSteps } from '../onboardingSteps.config'
import { CoachPreferencesSection } from '../../../../shared/coachPreferences/CoachPreferencesSection'
import { coachCardWithPaddingSx } from '../../../../shared/coachPreferences/coachSurfaceStyles'
import { CoachFocusSwitch } from './components/CoachFocusSwitch'
import { CoachModeContent } from './components/CoachModeContent'
import { CoachPageHeader } from './components/CoachPageHeader'
import { CoachSessionFeedbackModal } from './components/CoachSessionFeedbackModal'
import { CoachSidePanel } from './components/CoachSidePanel'
import { resolveVoiceStatusLabelKey } from './coachVoiceStatus'
import { flowOfFocus, focusOfConversation, isConversationOfFocus, isFirstGoalSettingFor } from './coachFocusHelpers'
import { useCoachFocus } from './useCoachFocus'
import { useCoachInteraction } from './useCoachInteraction'

const CHAT_MIN_HEIGHT_MOBILE_PX = 420

export const AICoachAriaPage = () => {
  const { t } = useTranslation()
  const { data: conversationsData } = useCoachConversations()
  const { data: coachStatus } = useCoachStatus()
  const { data: goals } = useUserGoals()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  const { state } = useAppState()
  const isLeadershipOffered = coachStatus?.leadershipEnabled === true
  const { focus, setFocus, isLeadershipFocus } = useCoachFocus({
    userId: state.user?.id,
    isLeadershipOffered,
    isFirstGoalSetting: isFirstGoalSettingFor(coachStatus)
  })

  const coach = useCoachInteraction({ focus })
  const { chat, session, mode, isFirstGoalSetting, leaveForFocus } = coach

  // The switch never disagrees with the thread on screen, whichever way the two were changed.
  const selectConversation = useCallback(
    (conversationId: string) => {
      const chosen = conversationsData?.conversations.find((conversation) => conversation.id === conversationId)
      if (isLeadershipOffered && chosen) setFocus(focusOfConversation(chosen))

      chat.selectConversation(conversationId)
    },
    [chat, conversationsData, isLeadershipOffered, setFocus]
  )

  const isCoachStateLoading = !coachStatus || !conversationsData

  const allConversations = conversationsData?.conversations

  // With the leadership focus on offer the history follows the focus in use; otherwise it is every conversation.
  const shownConversations = useMemo(() => {
    if (!isLeadershipOffered) return allConversations ?? []

    return (allConversations ?? []).filter((conversation) => isConversationOfFocus(conversation, focus))
  }, [allConversations, focus, isLeadershipOffered])

  // The conversation panel already offers a way to begin, so the sidebar must not put a second one beside it.
  const isPanelOfferingStart = isSpokenAriaMode(mode) ? !coach.isVoiceActive : !chat.selectedConversation

  // Only worth pointing back to onboarding when something there is actually still outstanding —
  // read from a fresh fetch, since `state.user` is the sign-in copy and misses steps finished since.
  const { data: storedUserResponse } = useUser(state.user?.id ?? '', { staleTime: 0 })
  const showContinueOnboarding =
    coach.hasJustCompletedGoals && hasIncompleteOnboardingSteps(storedUserResponse?.data ?? state.user)

  // Who holds the turn drives both the status line and the avatar; a permanently pulsing orb says
  // nothing about whether anything is actually happening. Read from the live session, not the whole
  // conversation — the saved half is all finished turns and could never hold the floor.
  const speaker = getAriaTranscriptSpeaker(session.turns)
  const isAriaSpeaking = speaker === AssistantSpeakerRole.ASSISTANT

  // A plain create, not `openConversation` — this button exists to leave the current thread, so
  // resuming would make it do nothing. `startCoachConversation` refuses a second one before goals exist.
  const handleStartConversation = useCallback(
    () => void chat.startConversation(undefined, { flow: flowOfFocus(focus) }),
    [chat, focus]
  )
  const handleRetry = useCallback(
    (message: Parameters<typeof chat.retryMessage>[0]) => void chat.retryMessage(message),
    [chat]
  )
  // The page follows the switch at once: the other focus's thread, or a fresh start in it.
  const changeFocus = useCallback(
    (nextFocus: GoalScope) => {
      leaveForFocus(nextFocus)
      setFocus(nextFocus)
    },
    [leaveForFocus, setFocus]
  )

  const openSettings = useCallback(() => setSettingsOpen(true), [])
  const closeSettings = useCallback(() => setSettingsOpen(false), [])
  const openFeedback = useCallback(() => setFeedbackOpen(true), [])
  const closeFeedback = useCallback(() => setFeedbackOpen(false), [])

  const focusSwitch = isLeadershipOffered ? <CoachFocusSwitch focus={focus} onChange={changeFocus} /> : undefined

  return (
    <Box
      data-testid={TESTIDS.COACH_PAGE}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        gap: { xs: 2, sm: 2, md: 2.5 },
        p: { xs: 2, sm: 2.5, md: 3 }
      }}
    >
      <CoachPageHeader
        speaking={chat.isBusy || isAriaSpeaking}
        mode={mode}
        onModeChange={coach.setMode}
        onLanguageChanged={coach.restartVoiceForLanguage}
        onOpenFeedback={openFeedback}
        onOpenSettings={openSettings}
        focusSwitch={focusSwitch}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gap: { xs: 2, sm: 2, md: 2.5 },
          // The column sizes itself, so folding hands the width to the conversation rather than leaving a gap.
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'minmax(0, 1fr)',
            md: 'minmax(0, 1fr) auto'
          },
          overflowX: 'hidden',
          overflowY: { xs: 'auto', sm: 'auto', md: 'hidden' }
        }}
      >
        <Stack
          sx={{
            ...coachCardWithPaddingSx,
            minWidth: 0,
            minHeight: { xs: CHAT_MIN_HEIGHT_MOBILE_PX, sm: CHAT_MIN_HEIGHT_MOBILE_PX, md: 0 },
            overflow: 'hidden'
          }}
        >
          <CoachModeContent
            mode={mode}
            chat={chat}
            session={session}
            isVoiceActive={coach.isVoiceActive}
            isFirstGoalSetting={isFirstGoalSetting}
            isLeadershipFocus={isLeadershipFocus}
            hasJustCompletedGoals={showContinueOnboarding}
            statusLabel={t(
              resolveVoiceStatusLabelKey({
                status: session.status,
                hasError: Boolean(session.error),
                speaker,
                isThinking: session.isAriaThinking
              })
            )}
            onStartVoice={coach.startVoice}
            onStopVoice={coach.stopVoice}
            onUseText={() => coach.setMode(AriaInteractionMode.TEXT)}
            onStartConversation={coach.openConversation}
            onSend={coach.sendTypedMessage}
            onRetry={handleRetry}
          />
        </Stack>

        <CoachSidePanel
          goals={goals ?? []}
          conversations={shownConversations}
          overallSummary={isLeadershipFocus ? undefined : conversationsData?.overallSummary}
          selectedId={chat.selectedId}
          liveMessageCount={coach.isVoiceActive ? session.turns.length : undefined}
          isFirstGoalSetting={isFirstGoalSetting}
          isPanelOfferingStart={isPanelOfferingStart}
          isLoading={isCoachStateLoading}
          goalScope={focus}
          onSelect={selectConversation}
          onNew={handleStartConversation}
        />
      </Box>

      <DefaultModal isOpen={settingsOpen} onClose={closeSettings} maxWidth="520px">
        <CoachPreferencesSection />
      </DefaultModal>

      <CoachSessionFeedbackModal isOpen={feedbackOpen} onClose={closeFeedback} />
    </Box>
  )
}

export default AICoachAriaPage
